import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	createExportXls,
	CreateExportXlsResult,
	CzMaxPriceResult,
	ErrorResponse,
	findCzMaxPrice,
	ProductUpdate,
	updateCzMaxPrice as apiUpdateCzMaxPrice,
	ValidationResult,
} from '~/Api/User/ProductGroup/CzMaxPrice';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';

const updateQueue = new PromiseQueue(1, Infinity);

export function fetchCzMaxPrice(groupId: string): ThunkAction<Promise<CzMaxPriceResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findCzMaxPrice, userId, groupId));
		dispatch({ type: ActionType.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				type: ActionType.receive,
				groupId,
				request,
				czMaxPrice: response?.czMaxPrice ?? null,
			})
		);
	};
}

export function updateCzMaxPrice(
	groupId: string,
	packageSize: number | null,
	basketVersion: string,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return (dispatch) => {
		const request = updateQueue.add(() =>
			dispatch(immediatelyUpdateCzMaxPrice(groupId, packageSize, basketVersion, products))
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });
		return request;
	};
}

function immediatelyUpdateCzMaxPrice(
	groupId: string,
	packageSize: number | null,
	basketVersion: string,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(invokeApi(apiUpdateCzMaxPrice, userId, groupId, packageSize, basketVersion, products))
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				type: ActionType.receiveUpdate,
				groupId,
				request,
				czMaxPrice: response?.czMaxPrice ?? null,
			})
		);
	};
}

export function handleCzMaxPriceUpdateResponse(response: any, groupId: string): ThunkAction<void, Action> {
	return (dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}

		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchCzMaxPrice(groupId)).catch((response) => handleError(response));
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchCzMaxPrice(groupId)).catch((response) => handleError(response));
		}

		dispatch(handleError(response));
	};
}

export function createCzMaxPriceExportXls(groupId: string): ThunkAction<Promise<CreateExportXlsResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(createExportXls, userId, groupId));
		dispatch({ type: ActionType.requestXlsCreate, groupId, request });

		return resolve(request, (_, error) =>
			dispatch({
				type: ActionType.receiveXlsCreate,
				request,
				validationResult:
					error !== null
						? error instanceof ErrorResponse
							? error.toValidationResult()
							: null
						: new ValidationResult(false, [], []),
			})
		);
	};
}

export function pruneCzMaxPrice(groupId: string): Action {
	return { type: ActionType.prune, groupId };
}

export function scheduleCzMaxPriceUpdate(
	groupId: string,
	packageSize: number | null,
	basketVersion: string,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return (dispatch) => {
		return new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(dispatch(updateCzMaxPrice(groupId, packageSize, basketVersion, products)));
			}, 2000);

			dispatch({
				type: ActionType.scheduleUpdate,
				timeoutId,
				groupId,
				packageSize,
				basketVersion,
				products,
			});
		});
	};
}

export function commitCzMaxPriceUpdate(groupId: string): ThunkAction<Promise<void>, Action> {
	return async (dispatch, getState) => {
		const { update } = getState().czMaxPrice;
		if (update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { timeoutId, packageSize, basketVersion, products } = update.state;
			clearTimeout(timeoutId);
			await dispatch(updateCzMaxPrice(groupId, packageSize, basketVersion, products));
		}
	};
}

export function handleCommitError(error: any): ThunkAction<void, Action> {
	return (dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};
}
